(() => {
  const EXPLORE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8.66665 11.9997L12.6666 7.99975L8.66665 3.99976M12.3333 7.99975H3.33331" stroke="#1F2933" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function rootPath() {
    return window.Shopify?.routes?.root || '/';
  }

  function normalizeSku(value) {
    return String(value || '').trim().toLowerCase();
  }

  async function fetchJson(url) {
    const res = await fetch(url);
    if (!res.ok) return null;
    return res.json();
  }

  async function fetchAccessoryCatalog(handle) {
    const products = [];
    const limit = 250;
    for (let page = 1; page <= 8; page += 1) {
      const url = `${rootPath()}collections/${encodeURIComponent(handle)}/products.json?limit=${limit}&page=${page}`;
      const data = await fetchJson(url);
      const batch = data?.products || [];
      products.push(...batch);
      if (batch.length < limit) break;
    }
    return products;
  }

  function indexBySku(products) {
    const map = new Map();
    products.forEach((product) => {
      (product.variants || []).forEach((variant) => {
        const sku = normalizeSku(variant.sku);
        if (sku && !map.has(sku)) map.set(sku, { product, variant });
      });
    });
    return map;
  }

  async function lookupSkuViaSearch(sku) {
    const suggestUrl =
      `${rootPath()}search/suggest.json?q=${encodeURIComponent(sku)}` +
      '&resources[type]=product&resources[limit]=8&resources[options][fields]=title,product_type,variants.sku,vendor';
    const data = await fetchJson(suggestUrl);
    const hits = data?.resources?.results?.products || [];
    for (const hit of hits) {
      if (!hit?.handle) continue;
      const product = await fetchJson(`${rootPath()}products/${hit.handle}.js`);
      if (!product) continue;
      const variant = (product.variants || []).find(
        (item) => normalizeSku(item.sku) === normalizeSku(sku)
      );
      if (variant) return { product, variant };
    }
    return null;
  }

  function imageUrl(product, variant) {
    const fromVariant = variant?.featured_image?.src || variant?.featured_image;
    if (typeof fromVariant === 'string' && fromVariant) return fromVariant;
    if (fromVariant?.src) return fromVariant.src;
    if (product.featured_image?.url) return product.featured_image.url;
    if (typeof product.featured_image === 'string') return product.featured_image;
    const img = (product.images || [])[0];
    if (typeof img === 'string') return img;
    return img?.src || '';
  }

  function isAvailable(product, variant) {
    if (typeof variant?.available === 'boolean') return variant.available;
    if (typeof product.available === 'boolean') return product.available;
    return true;
  }

  function productUrl(product) {
    if (product.url) return product.url.split('?')[0];
    return `${rootPath()}products/${product.handle}`;
  }

  function renderCard({ product, variant }) {
    const card = document.createElement('div');
    card.className = 'accessory-card featured-products__item';
    card.dataset.handle = product.handle || '';

    const url = productUrl(product);
    const title = product.title || '';
    const vendor = product.vendor || '';
    const sku = variant.sku || '';
    const src = imageUrl(product, variant);
    const available = isAvailable(product, variant);
    const stockLabel = available ? 'In Stock' : 'Out of Stock';
    const stockClass = available ? '' : ' out-of-stock';
    let imageAlt = title;
    if (vendor && !title.toLowerCase().includes(vendor.toLowerCase())) {
      imageAlt = `${vendor} ${title}`;
    }

    const image = src
      ? `<img src="${src}" alt="${escapeHtml(imageAlt)}" class="accessory-image" loading="lazy" width="600" height="180">`
      : '';

    card.innerHTML = `
      <div class="accessory-card__image">
        <div class="stock-status">
          <span class="product-card-badge product-card-badge--stock${stockClass}">
            <span class="product-card-badge__dot"></span> ${stockLabel}
          </span>
        </div>
        <a href="${url}">${image}</a>
      </div>
      <div class="accessory-card__content">
        <h3 class="title"><a href="${url}"></a></h3>
        <div class="sku"></div>
        <a href="${url}" class="explore-btn">Explore Product ${EXPLORE_SVG}</a>
      </div>
    `;
    card.querySelector('.title a').textContent = title;
    card.querySelector('.sku').textContent = sku;
    return card;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  async function enhance(root) {
    const grid = root.querySelector('[data-accessories-grid]');
    if (!grid) return;

    let skus = [];
    try {
      skus = JSON.parse(root.dataset.skus || '[]');
    } catch (error) {
      skus = [];
    }
    skus = (skus || []).map((sku) => String(sku).trim()).filter(Boolean);
    if (!skus.length) return;

    const limit = Number(root.dataset.limit) || 12;
    const excludeId = String(root.dataset.excludeId || '');
    const foundHandles = new Set(
      (root.dataset.foundHandles || '')
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean)
    );

    const collectionHandle = root.dataset.collectionHandle || 'accessories';
    let catalog = [];
    try {
      catalog = await fetchAccessoryCatalog(collectionHandle);
    } catch (error) {
      catalog = [];
    }
    const bySku = indexBySku(catalog);

    const missing = [];
    skus.forEach((sku) => {
      if (!bySku.has(normalizeSku(sku))) missing.push(sku);
    });
    for (const sku of missing) {
      try {
        const hit = await lookupSkuViaSearch(sku);
        if (hit) bySku.set(normalizeSku(sku), hit);
      } catch (error) {
        // Skip SKUs that cannot be resolved.
      }
    }

    let shown = grid.querySelectorAll('.accessory-card').length;
    skus.forEach((sku) => {
      if (shown >= limit) return;
      const hit = bySku.get(normalizeSku(sku));
      if (!hit) return;
      const productId = String(hit.product.id || '');
      const handle = hit.product.handle || '';
      if (excludeId && productId === excludeId) return;
      if (handle && foundHandles.has(handle)) return;
      grid.appendChild(renderCard(hit));
      if (handle) foundHandles.add(handle);
      shown += 1;
    });

    if (shown === 0) {
      root.setAttribute('hidden', '');
    } else {
      root.removeAttribute('hidden');
    }
    requestAnimationFrame(() => syncArrows(root));
  }

  function syncArrows(root) {
    const grid = root.querySelector('[data-accessories-grid]');
    const arrows = root.querySelector('[data-accessories-arrows]');
    if (!grid || !arrows) return;
    arrows.hidden = grid.scrollWidth <= grid.clientWidth + 4;
  }

  function bindArrows(root) {
    const grid = root.querySelector('[data-accessories-grid]');
    if (!grid || root.dataset.arrowsBound === 'true') return;
    root.dataset.arrowsBound = 'true';
    root.querySelectorAll('[data-accessories-arrow]').forEach((button) => {
      button.addEventListener('click', () => {
        const card = grid.querySelector('.accessory-card');
        const gap = 20;
        const step = card ? card.getBoundingClientRect().width + gap : grid.clientWidth;
        grid.scrollBy({ left: step * Number(button.dataset.dir), behavior: 'smooth' });
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-compatible-accessories]').forEach((root) => {
      bindArrows(root);
      enhance(root);
    });
    window.addEventListener('resize', () => {
      document.querySelectorAll('[data-compatible-accessories]').forEach(syncArrows);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
